import React, { useEffect, useRef, useState } from 'react'
import {
	ArrowPathIcon,
	Cog6ToothIcon,
	PauseIcon,
	PlayIcon,
	SpeakerWaveIcon,
	SpeakerXMarkIcon,
} from '@heroicons/react/24/outline'
import { useMutation } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { getConvexToken } from '../../convex'
import { jwtDecode } from 'jwt-decode'

const REFRESH_INTERVAL_OPTIONS = [15, 30, 60, 300]

const fmtInterval = sec => (sec >= 60 ? `${sec / 60}m` : `${sec}s`)

const fmtRemaining = sec => {
	if (sec >= 60) {
		const m = Math.floor(sec / 60)
		const s = sec % 60
		return `${m}:${String(s).padStart(2, '0')}`
	}
	return `${sec}s`
}

function AutoRefreshControl({
	tokens,
	isLight,
	onRefresh,
	extensionSyncEnabled,
	onExtensionSyncEnabledChange,
}) {
	const [enabled, setEnabled] = useState(true)
	const [intervalSec, setIntervalSec] = useState(30)
	const [elapsedMs, setElapsedMs] = useState(0)
	const [settingsOpen, setSettingsOpen] = useState(false)
	const startRef = useRef(Date.now())
	const wrapRef = useRef(null)

	// Check if user is dev/admin
	const isDevUser = (() => {
		try {
			const jwtToken = localStorage.getItem('jwtToken')
			if (!jwtToken) return false
			const decoded = jwtDecode(jwtToken)
			return decoded?.userType === 'dev'
		} catch {
			return false
		}
	})()

	// Convex mutation for test notifications
	const createTestNotifications = useMutation(api.notifications.createTestNotifications)
	const [testLoading, setTestLoading] = useState(false)
	const [testMessage, setTestMessage] = useState(null)

	// Load notification settings from localStorage
	const [soundEnabled, setSoundEnabled] = useState(() => {
		try {
			const settings = localStorage.getItem('loadboard:notificationSettings')
			return settings ? JSON.parse(settings).soundEnabled ?? false : false
		} catch {
			return false
		}
	})
	const [popupEnabled, setPopupEnabled] = useState(() => {
		try {
			const settings = localStorage.getItem('loadboard:notificationSettings')
			return settings ? JSON.parse(settings).popupEnabled ?? false : false
		} catch {
			return false
		}
	})
	const [popupDuration, setPopupDuration] = useState(() => {
		try {
			const settings = localStorage.getItem('loadboard:notificationSettings')
			return settings ? JSON.parse(settings).popupDuration ?? 5000 : 5000
		} catch {
			return 5000
		}
	})

	// Save notification settings to localStorage whenever they change
	useEffect(() => {
		try {
			const settings = {
				soundEnabled,
				popupEnabled,
				popupDuration
			}
			localStorage.setItem('loadboard:notificationSettings', JSON.stringify(settings))
		} catch (error) {
			console.error('Error saving notification settings:', error)
		}
	}, [soundEnabled, popupEnabled, popupDuration])

	useEffect(() => {
		if (!enabled) {
			setElapsedMs(0)
			return
		}
		startRef.current = Date.now()
		setElapsedMs(0)
		const tick = () => {
			const elapsed = Date.now() - startRef.current
			if (elapsed >= intervalSec * 1000) {
				onRefresh?.()
				startRef.current = Date.now()
				setElapsedMs(0)
			} else {
				setElapsedMs(elapsed)
			}
		}
		const id = setInterval(tick, 250)
		return () => clearInterval(id)
	}, [enabled, intervalSec, onRefresh])

	useEffect(() => {
		if (!settingsOpen) return
		const onClick = e => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) setSettingsOpen(false)
		}
		document.addEventListener('mousedown', onClick)
		return () => document.removeEventListener('mousedown', onClick)
	}, [settingsOpen])

	const totalMs = intervalSec * 1000
	const progress = enabled ? Math.min(1, elapsedMs / totalMs) : 0
	const remainingSec = Math.max(0, Math.ceil((totalMs - elapsedMs) / 1000))

	const handleManualRefresh = () => {
		onRefresh?.()
		startRef.current = Date.now()
		setElapsedMs(0)
	}

	const handleTestNotifications = async () => {
		setTestLoading(true)
		setTestMessage(null)

		try {
			const token = getConvexToken()
			if (!token) {
				setTestMessage('Not authenticated')
				setTestLoading(false)
				return
			}

			const result = await createTestNotifications({ token, count: 3 })
			setTestMessage(`✓ ${result.message}`)

			// Clear message after 3 seconds
			setTimeout(() => setTestMessage(null), 3000)
		} catch (error) {
			console.error('Test notification error:', error)
			setTestMessage(`✗ ${error.message || 'Failed to create test notifications'}`)

			// Clear error after 5 seconds
			setTimeout(() => setTestMessage(null), 5000)
		} finally {
			setTestLoading(false)
		}
	}

	const btnHover = isLight ? 'hover:bg-gray-100' : 'hover:bg-white/[0.08]'
	const ringTrack = isLight ? 'text-gray-300' : 'text-white/15'
	const ringFill = isLight ? 'text-sky-500' : 'text-sky-400'

	const ringSize = 22
	const stroke = 1.5
	const r = (ringSize - stroke) / 2
	const c = 2 * Math.PI * r

	return (
		<div ref={wrapRef} className="relative inline-flex items-center gap-1.5">
			<button
				type="button"
				onClick={handleManualRefresh}
				title={enabled ? `Refresh now · next in ${fmtRemaining(remainingSec)}` : 'Refresh now'}
				aria-label="Refresh now"
				className={`relative inline-flex items-center justify-center w-[26px] h-[26px] rounded-md ${btnHover}`}
			>
				<svg width={ringSize} height={ringSize} className="absolute inset-0 m-auto">
					<circle
						cx={ringSize / 2}
						cy={ringSize / 2}
						r={r}
						fill="none"
						stroke="currentColor"
						strokeWidth={stroke}
						className={ringTrack}
					/>
					{enabled && (
						<circle
							cx={ringSize / 2}
							cy={ringSize / 2}
							r={r}
							fill="none"
							stroke="currentColor"
							strokeWidth={stroke}
							strokeDasharray={c}
							strokeDashoffset={c * (1 - progress)}
							strokeLinecap="round"
							transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
							className={ringFill}
						/>
					)}
				</svg>
				<ArrowPathIcon className={`relative h-3 w-3 ${tokens.valueDim}`} strokeWidth={2.25} />
			</button>

			<button
				type="button"
				onClick={() => setEnabled(v => !v)}
				title={enabled ? 'Pause auto-refresh' : 'Resume auto-refresh'}
				aria-label={enabled ? 'Pause auto-refresh' : 'Resume auto-refresh'}
				className={`inline-flex items-center justify-center w-[30px] h-[30px] rounded-md ${btnHover}`}
			>
				{enabled ? (
					<PauseIcon className={`h-[18px] w-[18px] ${tokens.valueDim}`} strokeWidth={2.25} />
				) : (
					<PlayIcon className={`h-[18px] w-[18px] ${tokens.valueDim}`} strokeWidth={2.25} />
				)}
			</button>

			<button
				type="button"
				onClick={() => setSoundEnabled(v => !v)}
				title={soundEnabled ? 'Mute notification sound' : 'Enable notification sound'}
				aria-label={soundEnabled ? 'Mute notification sound' : 'Enable notification sound'}
				className={`inline-flex items-center justify-center w-[30px] h-[30px] rounded-md ${btnHover}`}
			>
				{soundEnabled ? (
					<SpeakerWaveIcon
						className={`h-[18px] w-[18px] ${isLight ? 'text-sky-600' : 'text-sky-400'}`}
						strokeWidth={2}
					/>
				) : (
					<SpeakerXMarkIcon className={`h-[18px] w-[18px] ${tokens.valueMuted}`} strokeWidth={2} />
				)}
			</button>

			<button
				type="button"
				onClick={() => setSettingsOpen(o => !o)}
				title="Auto-refresh settings"
				aria-label="Auto-refresh settings"
				className={`inline-flex items-center justify-center w-[30px] h-[30px] rounded-md ${btnHover}`}
			>
				<Cog6ToothIcon className={`h-[18px] w-[18px] ${tokens.valueDim}`} strokeWidth={2} />
			</button>

			{settingsOpen && (
				<div
					className={`absolute right-0 top-full mt-1 z-30 w-64 rounded-md p-3 ${tokens.dropdownBg}`}
				>
					{/* Refresh Interval */}
					<div className={`mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider ${tokens.valueMuted}`}>
						Refresh interval
					</div>
					<div className="grid grid-cols-4 gap-1">
						{REFRESH_INTERVAL_OPTIONS.map(s => {
							const active = s === intervalSec
							return (
								<button
									key={s}
									type="button"
									onClick={() => setIntervalSec(s)}
									className={`text-xs font-medium py-1 rounded-md transition-colors ${
										active
											? 'bg-sky-500 text-white'
											: isLight
												? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
												: 'bg-white/[0.05] text-gray-300 hover:bg-white/[0.1]'
									}`}
								>
									{fmtInterval(s)}
								</button>
							)
						})}
					</div>

					{/* Extension Filter Sync */}
					<div className={`mt-3 pt-3 border-t ${tokens.dropdownDivider}`}>
						<div className="flex items-center justify-between">
							<div>
								<div className={`text-xs font-medium ${tokens.valueText}`}>
									Live load fetching
								</div>
							</div>
							<button
								type="button"
								onClick={() =>
									onExtensionSyncEnabledChange?.(!extensionSyncEnabled)
								}
								aria-label={
									extensionSyncEnabled
										? 'Disable extension load sync'
										: 'Enable extension load sync'
								}
								aria-pressed={extensionSyncEnabled}
								className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
									extensionSyncEnabled
										? 'bg-sky-500'
										: isLight
											? 'bg-gray-200'
											: 'bg-white/10'
								}`}
							>
								<span
									className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
										extensionSyncEnabled
											? 'translate-x-5'
											: 'translate-x-0.5'
									}`}
								/>
							</button>
						</div>
					</div>

					{/* Notification Popup */}
					<div className={`mt-3 pt-3 border-t ${tokens.dropdownDivider}`}>
						<div className="flex items-center justify-between mb-2">
							<span className={`text-xs font-medium ${tokens.valueText}`}>
								Show notification popup
							</span>
							<button
								type="button"
								onClick={() => setPopupEnabled(v => !v)}
								className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
									popupEnabled
										? 'bg-sky-500'
										: isLight ? 'bg-gray-200' : 'bg-white/10'
								}`}
							>
								<span
									className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
										popupEnabled ? 'translate-x-5' : 'translate-x-0.5'
									}`}
								/>
							</button>
						</div>
						{popupEnabled && (
							<div className="mt-2">
								<label className={`text-xs ${tokens.valueDim} block mb-1`}>
									Auto-close after
								</label>
								<select
									value={popupDuration}
									onChange={(e) => setPopupDuration(parseInt(e.target.value))}
									className={`w-full text-xs py-1 px-2 rounded-md border transition-colors ${
										isLight
											? 'bg-white border-gray-300 text-gray-900'
											: 'bg-white/5 border-white/10 text-gray-200'
									}`}
								>
									<option value={3000}>3 seconds</option>
									<option value={5000}>5 seconds</option>
									<option value={7000}>7 seconds</option>
									<option value={10000}>10 seconds</option>
									<option value={0}>Never (manual close)</option>
								</select>
							</div>
						)}
					</div>

					{/* Admin Test Notifications - DEV ONLY */}
					{isDevUser && (
						<div className={`mt-3 pt-3 border-t ${tokens.dropdownDivider}`}>
							<div className={`mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider ${tokens.valueMuted}`}>
								Testing (Admin)
							</div>
							<button
								type="button"
								onClick={handleTestNotifications}
								disabled={testLoading}
								className={`w-full text-xs font-medium py-2 px-3 rounded-md transition-colors ${
									testLoading
										? isLight
											? 'bg-gray-100 text-gray-400 cursor-not-allowed'
											: 'bg-white/[0.05] text-gray-500 cursor-not-allowed'
										: isLight
											? 'bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200'
											: 'bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 border border-sky-500/20'
								}`}
							>
								{testLoading ? 'Creating test notifications...' : '🧪 Create Test Notifications'}
							</button>
							{testMessage && (
								<div
									className={`mt-2 text-[11px] px-2 py-1.5 rounded ${
										testMessage.startsWith('✓')
											? isLight
												? 'bg-green-50 text-green-700 border border-green-200'
												: 'bg-green-500/10 text-green-400 border border-green-500/20'
											: isLight
												? 'bg-red-50 text-red-700 border border-red-200'
												: 'bg-red-500/10 text-red-400 border border-red-500/20'
									}`}
								>
									{testMessage}
								</div>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	)
}

export default AutoRefreshControl
